import React, { useContext, useEffect, useState } from "react";
import StoreMenu from "../../StoreMenu";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../../../utils/localStorage";
import {
  getBrandStore,
  getBrandUsers,
} from "../../../services/StoreUsers/PostStoreUsers";
import { formatDate } from "../../../constants/DateUtil/DateUtil";
import { ProgressSpinner } from "primereact/progressspinner";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { getBrandWarehouse } from "../../../services/warehouse/Warehouse";
import { getPaymentGatewayConfig } from "../../../services/PaymentGatewayConfig/PaymentGatewayConfig";
import AdminMenu from "../../AdminMenu";

import {
  Pencil,
  Eye,
  EyeOff,
  ExternalLink,
  Mail, 
} from "lucide-react";

// ---- design tokens -------------------------------------------------------
const ink = "#0F2337";
const inkSoft = "#173654";
const paper = "#EFF3F1";
const cardLine = "#DCE3DF";
const teal = "#0E7C6B";
const tealBg = "#E1F1EC";
const inactiveText = "#8B8F86";
const inactiveBg = "#EDEEE9";
const slate = "#5C6B72";
const slateDark = "#1E2B32";
const danger = "#B23B2E";

const fontDisplay = "'Space Grotesk', sans-serif";
const fontMono = "'IBM Plex Mono', monospace";
const fontBody = "'Inter', sans-serif";

const PaymentGatewayConfigView = () => {
  const navigate = useNavigate();
  const [paymentGatewayInfoData, setPaymentGatewayInfoData] = useState([]);
  const [visible, setVisible] = useState(false);

  const { brandProfileData } = getUserData();
  let brandId = brandProfileData.id;
  const [loading, setLoading] = useState(false);

  const fetchPaymentGatewayConfig = data => {
    setLoading(true);
    getPaymentGatewayConfig(data)
      .then(response => {
        if (!response.data.error) {
          const Res = response.data;
          setPaymentGatewayInfoData(Res);
          setLoading(false);
        }
      })
      .catch(err => {
        console.error('Error:', err);
      });
  };
  useEffect(() => {
    fetchPaymentGatewayConfig(brandId);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');

  const filterPaymentGatewayList = () => {
    return paymentGatewayInfoData.filter(item => {
      const fullName = item.gatewayName?.toLowerCase();
      const matchesSearchTerm = fullName?.includes(searchTerm.toLowerCase());
      return matchesSearchTerm;
    });
  };

  const filteredPaymentGatewayData = filterPaymentGatewayList();
  const [visibleSecrets, setVisibleSecrets] = useState(new Set());

  const toggleSecret = (id) => {
    setVisibleSecrets((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const TearLine = () => (
    <div style={{ position: "relative", height: 1, margin: "10px 0" }}>
      <div
        style={{
          borderTop: `1px dashed ${cardLine}`,
          width: "100%",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: -17,
          top: -9,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: paper,
        }}
      />
      <div
        style={{
          position: "absolute",
          right: -17,
          top: -9,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: paper,
        }}
      />
    </div>
  );

  const Field = ({ label, value, mono = true }) => (
    <div style={{ minWidth: 0 }}>
      <p
        style={{
          margin: 0,
          fontSize: 13,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: slate,
          fontFamily: fontBody,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "2px 0 0",
          fontSize: 16,
          color: slateDark,
          fontFamily: mono ? fontMono : fontBody,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={value}
      >
        {value || "—"}
      </p>
    </div>
  );

  function GatewayCard({ item, secretVisible, onToggleSecret }) {
    const isActive = item.status === "ACTIVE";
    return (
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          border: `1px solid ${cardLine}`,
          borderRadius: 14,
          padding: "14px 16px 16px",
          marginBottom: 14,
        }}
      >
        {/* header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isActive ? teal : inactiveText,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: fontDisplay,
                fontWeight: 600,
                fontSize: 20,
                color: slateDark,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.gatewayName}
            </p>
            <p
              style={{
                margin: "1px 0 0",
                fontSize: 15,
                color: slate,
                fontFamily: fontBody,
              }}
            >
              {item.currency} · {item.baseUrl}
            </p>
          </div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              padding: "3px 8px",
              borderRadius: 20,
              background: isActive ? tealBg : inactiveBg,
              color: isActive ? "#085041" : "#5f5f56",
              fontFamily: fontBody,
              whiteSpace: "nowrap",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
          <button
            aria-label={`Edit ${item.gatewayName}`}
            onClick={() => navigate("/PaymentGatewayConfigCreate", {
              state: {
                item: item,
              },
            })}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#2B70D7",
              padding: 4,
              display: "flex",
            }}
          >
            <Pencil size={18} />
          </button>
        </div>

        <TearLine />

        {/* credential grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 12px",
          }}
        >
          <Field label="Client key ID" value={item.clientKeyId} />
          <Field label="Merchant ID" value={item.merchantId} />
          <Field label="Aggregator ID" value={item.aggregatorId} />
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: slate,
                fontFamily: fontBody,
              }}
            >
              Client secret
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 16,
                  color: slateDark,
                  fontFamily: fontMono,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {secretVisible ? item.clientKeySecret : "•".repeat(14)}
              </p>
              <button
                aria-label={secretVisible ? "Hide secret" : "Show secret"}
                onClick={() => onToggleSecret(item.id)}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: slate, padding: 2, display: "flex" }}
              >
                {secretVisible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        <TearLine />

        {/* links / notify */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 12px",
          }}
        >
          <Field label="Return URL" value={item.returnURL} mono={false} />
          <Field label="Payment link URL" value={item.initiatePaymentLinkURL} mono={false} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Mail size={14} color={slate} style={{ flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 15, color: slate, fontFamily: fontBody }}>
              Notifications to {item.responseOnEmailId}
            </p>
          </div>
           
        </div>

        
      </div>
    );
  }

  const renderCardItem = (data) => {
    return data?.map((item, index) => {
      return (
        <div>
          <div className="flex-1 ">
            <div className="m-4 shadow-4">
              <div className="flex flex-1">
                <div
                  className="flex-1 px-3 py-1"
                  style={{
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Gateway Name :
                  </p>
                  <p className="text-lg font-medium capitalize">{item.gatewayName}</p>
                </div>
                <div
                  className="flex-1 px-3 py-1"
                  style={{
                    backgroundColor: "#E2F4F8",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Currency :
                  </p>
                  <p className="text-lg font-medium capitalize">
                    {item.currency}
                  </p>
                </div>
                <div
                  className="flex-1 px-3 py-1 flex justify-content-between"
                  style={{
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <div>
                    <p
                      className="text-md font-normal capitalize"
                      style={{ color: "#75879B" }}
                    >
                      Client Key Id :
                    </p>
                    <p className="text-lg font-medium capitalize">
                      {item.clientKeyId}
                    </p>
                  </div>
                  <div
                    className="mr-4 mt-2"
                    onClick={() =>
                      navigate("/PaymentGatewayConfigCreate", {
                        state: {
                          item: item,
                        },
                      })
                    }
                  >
                    <i
                      className="pi pi-pencil"
                      style={{
                        fontSize: "25px",
                        cursor: "pointer",
                        color: "#2B70D7",
                      }}
                    ></i>
                  </div>

                </div>
              </div>
              <div className="flex flex-1">
                <div
                  className="flex-1 px-2 py-1"
                  style={{
                    backgroundColor: "#E2F4F8",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Client Key Secret:
                  </p>


                  <p className="text-lg font-medium capitalize">
                    {item.clientKeySecret}
                  </p>

                </div>
                <div
                  className=" px-3 py-1"
                  style={{
                    flex: 2,
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Return Url :
                  </p>
                  <p className="text-lg font-medium capitalize">
                    {item.returnURL}
                  </p>
                </div>
              </div>
              <div className="flex flex-1">
                <div
                  className="flex-1 px-3 py-1"
                  style={{
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Aggregator Id :
                  </p>
                  <p className="text-lg font-medium capitalize">{item.aggregatorId}</p>
                </div>
                <div
                  className="flex-1 px-3 py-1"
                  style={{
                    backgroundColor: "#E2F4F8",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Merchant Id :
                  </p>
                  <p className="text-lg font-medium capitalize">
                    {item.merchantId}
                  </p>
                </div>
                <div
                  className="flex-1 px-3 py-1 flex justify-content-between"
                  style={{
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <div>
                    <p
                      className="text-md font-normal capitalize"
                      style={{ color: "#75879B" }}
                    >
                      Base URL :
                    </p>
                    <p className="text-lg font-medium capitalize">
                      {item.baseUrl}
                    </p>
                  </div>

                </div>
              </div>
              <div className="flex flex-1">
                <div
                  className="flex-1 px-2 py-1"
                  style={{
                    backgroundColor: "#E2F4F8",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Response On Email Id:
                  </p>


                  <p className="text-lg font-medium capitalize">
                    {item.responseOnEmailId}
                  </p>

                </div>
                <div
                  className=" px-3 py-1"
                  style={{
                    flex: 2,
                    backgroundColor: "#F8FFEE",
                  }}
                >
                  <p
                    className="text-md font-normal capitalize"
                    style={{ color: "#75879B" }}
                  >
                    Initiate Payment Link Url :
                  </p>
                  <p className="text-lg font-medium capitalize">
                    {item.initiatePaymentLinkURL}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="flex-1">
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex ">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() => navigate("/Setting")}
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-2xl text-white font-bold uppercase">
                Payment Gateway
              </p>
            </div>
          </div>

          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      <div className="my-2 mx-5 flex flex-1">
        <div
          onClick={() =>
            navigate("/PaymentGatewayConfigCreate", {
              state: {
                item: null,
              },
            })
          }
          className="flex justify-content-center align-items-center mx-2 cursor-pointer"
        >
          <div>
            <img
              src={
                "Laundy_App_Icons_Gifs/Home_Page_Menu_Bar/Quick_add_customer.png"
              }
              style={{ width: 27 }}
            />
          </div>
        </div>
        <div className="flex-1">
          <IconField iconPosition="left" className="flex flex-1">
            <InputIcon className="pi pi-search text-xl"> </InputIcon>
            <InputText
              placeholder={"Search Payment Gateway"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-xl flex flex-1 py-1 px-5"
              style={{ borderRadius: 30 }}
            />
          </IconField>
        </div>

        <div
          className="flex justify-content-center align-items-center mx-2 cursor-pointer"
          onClick={() => setSearchTerm("")}
        >
          <div>
            <p className="text-4xl font-bold " style={{ color: "red" }}>
              X
            </p>
          </div>
        </div>
      </div>
      <div>
        {loading ? (
          <div style={{ flex: 1 }}>
            <div className="text-center">
              <ProgressSpinner
                style={{ width: "70px", height: "70px" }}
                strokeWidth="4"
                fill="var(--surface-ground)"
                animationDuration="2s"
              />
            </div>
          </div>
        ) : paymentGatewayInfoData?.length > 0 ? (
          <div  className="m-4">
            {(filteredPaymentGatewayData || paymentGatewayInfoData).map((item) => (
              <GatewayCard
                key={item.id}
                item={item}
                secretVisible={visibleSecrets.has(item.id)}
                onToggleSecret={toggleSecret}
              />))}
            <div>{/*renderCardItem(filteredPaymentGatewayData || paymentGatewayInfoData)*/}</div>
          </div>
        ) : (
          <div className="flex justify-content-center align-items-center">
            <p className="text-xl font-medium">No Payment Gateway Found</p>
          </div>
        )}
      </div>

      <AdminMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default PaymentGatewayConfigView;
